// Dependencies
import { nanoid } from "nanoid/non-secure";
import React, { MouseEvent, PropsWithChildren, forwardRef, useImperativeHandle, useMemo, useRef } from "react";

import { IconDefinition } from "@fortawesome/fontawesome-svg-core";
import { faCircleX } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import { TextSize } from "../../Text";
// Components
import { Info } from "../InputWrapper";
import InputWrapper from "../InputWrapper";
// Styles
import css from "./Input.module.scss";

type Size = "standard" | "medium" | "small";

interface BaseProps {
	/**
	 * The value of the input
	 */
	value: string | number;
	/**
	 * The size of the input
	 * @default standard
	 */
	size?: Size;
	/**
	 * The label to display above the input. This label will have a `for`
	 * attribute to specify which input it is bound to.
	 * @default undefined
	 */
	label?: string;
	/**
	 * The size of the label
	 */
	labelSize?: TextSize;
	/**
	 * The class name to apply to the input
	 */
	className?: string;
	/**
	 * The type of input to render
	 * @default text
	 */
	type?: "text" | "password" | "email" | "tel" | "url" | "number";
	/**
	 * The id of the input. If not provided, a random id will be generated
	 * by nanoid.
	 * @default undefined
	 */
	inputId?: string;
	/**
	 * A test id to apply to the input for testing purposes.
	 */
	testId?: string;
	/**
	 * An icon to display to the left of the input
	 * @default undefined
	 */
	icon?: IconDefinition;
	/**
	 * Whether or not to enable spell checking
	 * @default true
	 */
	spellCheck?: boolean;
	/**
	 * Whether or not the input is disabled
	 * @default false
	 * @remarks
	 * This is a HTML attribute, not a React prop. It is passed to the input
	 * element as an attribute.
	 * @see https://developer.mozilla.org/en-US/docs/Web/HTML/Element/input#htmlattrdefdisabled
	 */
	disabled?: boolean;
	/**
	 * Whether or not the input is required
	 * @default false
	 * @remarks
	 * This is a HTML attribute, not a React prop. It is passed to the input
	 * element as an attribute.
	 * @see https://developer.mozilla.org/en-US/docs/Web/HTML/Element/input#htmlattrdefrequired
	 */
	required?: boolean;
	/**
	 * The placeholder text to display in the input
	 * @default
	 */
	placeholder?: string;
	/**
	 * The maximum length of the input
	 * @default undefined
	 * @remarks
	 * This is a HTML attribute, not a React prop. It is passed to the input
	 * element as an attribute.
	 * @see https://developer.mozilla.org/en-US/docs/Web/HTML/Element/input#htmlattrdefmaxlength
	 */
	maxLength?: number;
	/**
	 * The name of the input
	 * @default undefined
	 * @remarks
	 * This is a HTML attribute, not a React prop. It is passed to the input
	 * element as an attribute.
	 * @see https://developer.mozilla.org/en-US/docs/Web/HTML/Element/input#htmlattrdefname
	 */
	name?: string;
	/**
	 * The color of the icon
	 * @default undefined
	 */
	iconColor?: string;
	/**
	 * The placement of the icon
	 */
	iconPlacement?: "left" | "right";
	/**
	 * The class name to apply to the icon
	 * @default undefined
	 */
	iconClass?: string;

	/**
	 * The class name to apply to the clear icon
	 * @default undefined
	 */
	clearIconClass?: string;

	/**
	 * should the input be clearable with a clear Icon
	 */
	clearable?: boolean;
	/**
	 * The style to apply to the input. Using className is preferred.
	 * @default undefined
	 */
	style?: React.CSSProperties;
	/**
	/* If true, this will outline the input with a warning color.
	 */
	showWarning?: boolean;
	/**
	 * An optional error message to display below the input. If this is
	 * provided, the input will be styled as if it has an error.
	 */
	errorMessage?: string;
	/**
	 * should the error message be animated
	 */
	animatedError?: boolean;
	/**
	 * An optional info message to display when hovered over the info icon.
	 */
	info?: Info;
	/**
	 * A handler for when the input value changes
	 */
	onChange?: (value: string) => void;
	/**
	 * A handler for when the input loses focus
	 */
	onBlur?: (event: React.FocusEvent<HTMLInputElement>) => void;
	/**
	 * A handler for when the input gains focus
	 */
	onFocus?: (event: React.FocusEvent<HTMLInputElement>) => void;
	/**
	 * A handler for the up stroke of a key.
	 * @remarks
	 * This is a HTML attribute, not a React prop. It is passed to the input
	 * element as an attribute.
	 * @see https://developer.mozilla.org/en-US/docs/Web/HTML/Element/input#htmlattrdefonkeyup
	 */
	onKeyUp?: (event: React.KeyboardEvent<HTMLInputElement>) => void;
	/**
	 * A handler for the down stroke of a key.
	 */
	onKeyDown?: (event: React.KeyboardEvent<HTMLInputElement>) => void;
	/**
	 * Callback on enter
	 */
	onEnter?: () => void;
	/**
	 * A handler for when the icon is clicked
	 */
	onClickIcon?: (event: MouseEvent) => void;
	/**
	 * The step attribute specifies the legal number intervals for an input field.
	 */
	step?: string | number;
	/**
	 * Autofocus the input
	 */
	autoFocus?: boolean;
	/**
	 * The image renderd to the right of the title
	 */
	titleImage?: string;
	/**
	 * href of the title
	 */
	titleHref?: string;

	/**
	 * tooltip for the label
	 */
	titleTooltip?: string;
}

/**
 * @deprecated use InputText in py-components instead
 * A controlled text input component
 */
const Input = forwardRef<HTMLInputElement, PropsWithChildren<BaseProps>>((props, ref) => {
	const {
		value,
		size = "standard",
		label,
		labelSize = "paragraph-3",
		className,
		type = "text",
		icon,
		disabled = false,
		required = false,
		spellCheck = true,
		placeholder = "",
		errorMessage = "",
		info,
		maxLength,
		name,
		style,
		onBlur,
		onChange,
		onKeyDown,
		onFocus,
		onKeyUp,
		onEnter,
		onClickIcon,
		iconColor,
		iconPlacement = "left",
		iconClass = "",
		clearIconClass = "",
		clearable = false,
		showWarning = false,
		testId,
		animatedError = false,
		step = "any",
		autoFocus,
		titleImage,
		titleHref,
		titleTooltip,
	} = props;

	const inputRef = useRef<HTMLInputElement>({} as HTMLInputElement);

	useImperativeHandle(ref, () => {
		return {
			...inputRef.current,
			focus() {
				inputRef.current.focus();
			},
		};
	}, []);

	const inputId = useMemo(() => {
		return props.inputId || nanoid();
	}, [props.inputId]);

	const inputClasses = [
		css.main,
		css[size],
		className,
		icon ? css.hasIcon : css.hasNoIcon,
		clearable && css.clearable,
	].join(" ");

	const changeHandler = (event: React.ChangeEvent<HTMLInputElement>) => {
		if (onChange) {
			onChange(event.target.value);
		}
	};

	const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
		if (event.key === "Enter") {
			onEnter && onEnter();
		}
		if (onKeyDown) {
			onKeyDown(event);
		}
	};

	const handleClear = () => {
		if (onChange) {
			onChange("");
		}
	};

	return (
		<InputWrapper
			labelSize={labelSize}
			label={label}
			info={info}
			inputId={inputId}
			errorMessage={errorMessage}
			animatedError={animatedError}
			titleImage={titleImage}
			titleHref={titleHref}
			titleTooltip={titleTooltip}
		>
			<div className={inputClasses}>
				<div
					className={` ${css.inputContent} ${disabled ? css.disabledInput : null} ${showWarning || errorMessage ? css.errorInput : null} ${iconPlacement === "left" ? css.iconLeft : css.iconRight} ${clearable && value.toString().length ? css.clearableShowing : ""} `}
				>
					{icon && iconPlacement === "left" ? (
						<div className={`${css.inputIcon} ${iconClass}`}>
							<FontAwesomeIcon
								icon={icon}
								color={iconColor}
								data-testid={icon?.iconName || "input-icon"}
							/>
						</div>
					) : null}
					<input
						data-testid={testId}
						placeholder={placeholder}
						disabled={disabled}
						required={required}
						value={value}
						id={inputId}
						className={css.input}
						ref={inputRef}
						type={type}
						spellCheck={spellCheck}
						maxLength={maxLength}
						name={name}
						style={style}
						onBlur={onBlur}
						onFocus={onFocus}
						onKeyUp={onKeyUp}
						onChange={changeHandler}
						onKeyDown={handleKeyDown}
						step={step}
						autoFocus={autoFocus}
					/>
					{clearable && value.toString().length > 0 ? (
						<div
							className={`${css.inputIcon} ${css.clearButton} ${iconClass} ${clearIconClass}`}
							onClick={handleClear}
						>
							<FontAwesomeIcon icon={faCircleX} color={iconColor} />
						</div>
					) : null}
					{icon && iconPlacement === "right" ? (
						<div data-testid="input-icon" className={`${css.inputIcon} ${iconClass}`} onClick={onClickIcon}>
							<FontAwesomeIcon icon={icon} color={iconColor} />
						</div>
					) : null}
				</div>
			</div>
		</InputWrapper>
	);
});

Input.displayName = "Input";

export default Input;
